/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    DropDown,
    getFilterElementsDefault,
    useWorkplaceState,
    WorkplaceBtnActions,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectList } from 'axios/brandApi';
import { getDictList } from 'axios/dictionaryApi';
import {
    addOrChangeTranslationItem,
    deleteTranslatorItem,
    getTranslateTypes,
    getTranslationItem,
    getTranslations,
    getTranslationsExcel,
} from 'axios/translatorApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import ButtonExcelFileUpload from 'components/ExcelFileUpload/ButtonExcelFileUpload';
import useApi from 'hooks/useApi';
import { map } from 'mappers/mapper';
import { translatorToWorkplace } from 'mappers/translator';
import DictItem from 'models/api/dictItem';
import {
    TranslateAdd,
    TranslateChange,
    TranslateDelete,
    TranslateFilterModel,
    TranslationWorkplace,
} from 'models/translateWorkplace';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useCallback, useState } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import TranslateAddButton from './TranslateAddButton';
import TranslateChangeModal from './TranslateChangeModal';
import TranslateDeleteBtn from './TranslateDeleteBtn';
import TranslateFilter from './TranslateFilter';

type TranslationFormatterProps = FormatterProps<TranslationWorkplace, TranslationWorkplace[]>;

const Translation = () => {
    const { t } = useTranslation();
    const { cultures } = useGeneralSettingsContext();
    const api = useApi();
    const queryClient = useQueryClient();

    const [translateCodeList, setTranslateCodeList] = useState<DropDown[]>([]);
    const [dictList, setDictList] = useState<DropDown[]>([]);
    const [brandList, setBrandList] = useState<DropDown[]>([]);

    const { isFetching: loadingDicts } = useQuery('dictList', () => getDictList(api), {
        onSuccess: (data) => {
            setDictList(data.map((x) => ({ id: x.code, name: `[${x.code}]: ${x.name}` })));
        },
        enabled: !dictList.length,
    });

    const { isFetching: loadingTranslateCodes } = useQuery(
        'translateCodeList',
        () => getTranslateTypes(api),
        {
            onSuccess: (data) => {
                setTranslateCodeList(data.map((x) => ({ id: x, name: x })));
            },
            enabled: !translateCodeList.length,
        }
    );

    const { isFetching: loadingBrands } = useQuery('brandList', () => getSelectList(api), {
        onSuccess: (data) => {
            setBrandList(data);
        },
        enabled: !brandList.length,
    });

    const [isModalVisible, setIsModalVisible] = useState(false);

    const brandCodeFormatter = useCallback(
        ({ row: { brandCode } }: TranslationFormatterProps) => (
            <>{brandList.find((b) => b.id === brandCode)?.name ?? brandCode}</>
        ),
        [brandList]
    );

    const culturesFormatter = useCallback((i: DictItem) => {
        const component = ({ row: { translations } }: TranslationFormatterProps) => (
            <>{translations[i.code]}</>
        );
        return component;
    }, []);

    const actionsFormatter = useCallback(
        ({ row }: TranslationFormatterProps) => (
            <WorkplaceBtnActions>
                <TranslateDeleteBtn
                    key={`delete-btn-${row.id}`}
                    item={{
                        brandCode: row.brandCode,
                        type: row.type,
                        name: row.name,
                    }}
                />
            </WorkplaceBtnActions>
        ),
        []
    );

    return (
        <BOWorkplace<
            TranslationWorkplace,
            TranslationWorkplace,
            TranslateFilterModel,
            TranslateChange,
            TranslateAdd,
            TranslateDelete
        >
            pageTitle="BackOfficeTranslator"
            filterParams={{
                brandCode: '',
                type: '',
                name: '',
                dictCode: '',
                dictItemCode: '',
                value: '',
                dictName: '',
            }}
            initColumns={[
                {
                    key: 'brandCode',
                    name: <Trans i18nKey="BackOfficeBrandName" />,
                    formatter: brandCodeFormatter,
                },
                { key: 'type', name: <Trans i18nKey="BackOfficeTranslateGroup" /> },
                { key: 'name', name: <Trans i18nKey="BackOfficeTranslateCode" /> },
                { key: 'dictCode', name: <Trans i18nKey="BackOfficeDict" /> },
                { key: 'dictItemCode', name: <Trans i18nKey="BackOfficeDictItem" /> },
                ...cultures.map((x) => ({
                    key: x.code,
                    name: x.name,
                    formatter: culturesFormatter(x),
                })),
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    resizable: false,
                    clickDisable: true,
                    formatter: actionsFormatter,
                },
            ]}
            storagePath="translate/columns"
            loadingFilter={loadingBrands || loadingDicts || loadingTranslateCodes}
            getFilterElements={getFilterElementsDefault([
                {
                    name: t('BackOfficeBrandName'),
                    id: 'brandCode',
                    formatter: (val) => brandList.find((b) => b.id === val)?.name ?? val,
                },
                { name: t('BackOfficeTranslateGroup'), id: 'type' },
                { name: t('BackOfficeTranslateCode'), id: 'name' },
                { name: t('BackOfficeTranslateValue'), id: 'value' },
                { name: t('BackOfficeDict'), id: 'dictName' },
                { name: t('BackOfficeDictName'), id: 'dictCode' },
                { name: t('BackOfficeDictItem'), id: 'dictItemCode' },
            ])}
            showModal={() => setIsModalVisible(true)}
            filter={
                <TranslateFilter
                    brandList={brandList}
                    loadingBrands={loadingBrands}
                    translateCodeList={translateCodeList}
                    loadingTranslateCodes={loadingTranslateCodes}
                    dictList={dictList}
                    loadingDicts={loadingDicts}
                />
            }
            modal={{
                component: isModalVisible && (
                    <TranslateChangeModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                fetchCurrentItemQueryFn: (id, item) => getTranslationItem(api, item),
                changeMutationFn: (item) => addOrChangeTranslationItem(api, item),
                addMutationFn: (item) => addOrChangeTranslationItem(api, item),
            }}
            fetchQueryFn={(filter) =>
                map(getTranslations(api, filter), (item) => translatorToWorkplace(item, cultures))
            }
            buttons={[
                <TranslateAddButton key="1" showModal={() => setIsModalVisible(true)} />,
                <ButtonExcelFileUpload key="2" controller="translator" />,
            ]}
            deleteMutationFn={(item) => deleteTranslatorItem(api, item)}
            queryKey={['translation']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getTranslationsExcel(api, filter)}
        />
    );
};

const useTranslateWorkplaceState = () =>
    useWorkplaceState<
        TranslationWorkplace,
        TranslationWorkplace,
        TranslateChange,
        TranslateAdd,
        TranslateDelete
    >();

export default Translation;
export { useTranslateWorkplaceState };
